import { Trash2, FileText, Lock } from 'lucide-react';
import { BookingDiscount } from '../../lib/discounts';
import { Payment } from '../../lib/payments';
import { paymentGross, paymentPrincipal, isPaymentCountedForBalance } from '../../lib/paymentBreakdown';
import { AdditionalTaxLine, getBookingAdditionalTaxBreakdown } from '../../lib/tax-rates';
import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useBookingInvoice } from '../../hooks/useBookingInvoice';
import { isInvoiceLocked } from '../../lib/invoices/getInvoiceForBooking';
import { totalLineDiscountCents } from '../../lib/invoices/lineDiscounts';

/** Format a basis-point rate (700 → "7", 750 → "7.5") for tax labels. */
function formatRateBps(bps: number): string {
  const pct = bps / 100;
  return Number.isInteger(pct) ? String(pct) : String(parseFloat(pct.toFixed(4)));
}

function discountDollarsForSummary(
  subtotal: number,
  discounts: BookingDiscount[] | undefined,
  discountAmountFromBooking?: number | null,
): number {
  if (discountAmountFromBooking != null && !Number.isNaN(Number(discountAmountFromBooking))) {
    const raw = Number(discountAmountFromBooking);
    if (raw <= 0) return 0;
    return Math.min(raw, subtotal);
  }
  if (!discounts?.length) return 0;
  return discounts.reduce((sum, d) => {
    if (d.type === 'percentage') {
      return sum + Math.round((subtotal * (d.amount / 100)) * 100) / 100;
    }
    return sum + Math.min(d.amount, subtotal);
  }, 0);
}

interface TaxRate {
  id: string;
  name: string;
  rate: number;
}

interface BookingPaymentSummaryProps {
  subtotal: number;
  /** Stored `bookings.discount_amount` — used so GST/PST match DB (discount $ / subtotal = effective %). */
  discountAmount?: number | null;
  discounts?: BookingDiscount[];
  taxAmount: number;
  totalAmount: number;
  payments?: Payment[];
  /** Booking services (with add-ons) so per-line discounts fold into the subtotal. */
  lineItems?: any[];
  businessId: string;
  bookingId?: string;
  onApplyDiscount: () => void;
  onDeleteDiscount: (discountId: string) => Promise<void>;
  onRecordPayment?: () => void;
  onViewInvoice?: () => void;
  compact?: boolean;
}

export default function BookingPaymentSummary({
  subtotal,
  discountAmount: discountAmountFromBooking,
  discounts,
  taxAmount,
  totalAmount,
  payments = [],
  lineItems = [],
  businessId,
  bookingId,
  onApplyDiscount,
  onDeleteDiscount,
  onRecordPayment,
  onViewInvoice,
  compact = false
}: BookingPaymentSummaryProps) {
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [additionalTaxLines, setAdditionalTaxLines] = useState<AdditionalTaxLine[]>([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    async function fetchTaxRates() {
      if (!businessId) return;
      
      const { data, error } = await supabase
        .from('tax_rates')
        .select('id, name, rate')
        .eq('business_id', businessId);

      if (!error && data) {
        setTaxRates(data);
      }
    }

    fetchTaxRates();
  }, [businessId]);

  useEffect(() => {
    if (bookingId) {
      getBookingAdditionalTaxBreakdown(bookingId)
        .then(lines => setAdditionalTaxLines(lines))
        .catch(() => setAdditionalTaxLines([]));
    }
  }, [bookingId, taxAmount]);

  const fmt = (n: number) => '$' + (Math.round(n * 100) / 100).toLocaleString();

  const discountDollars = useMemo(
    () => discountDollarsForSummary(subtotal, discounts, discountAmountFromBooking),
    [subtotal, discounts, discountAmountFromBooking],
  );

  const gstBase = useMemo(
    () => Math.max(0, Math.round((subtotal - discountDollars) * 100) / 100),
    [subtotal, discountDollars],
  );

  const additionalTaxTotal = additionalTaxLines.reduce((sum, l) => sum + l.amount, 0);

  const businessRateLines = useMemo(() => {
    const baseLabel = fmt(gstBase);
    const lines = taxRates.map((rate) => ({
      label: `${rate.name} (${rate.rate}% on ${baseLabel})`,
      amount: Math.round((gstBase * rate.rate) / 100 * 100) / 100,
    }));
    const bizSum = lines.reduce((s, l) => s + l.amount, 0);
    const diff = Math.round((taxAmount - bizSum - additionalTaxTotal) * 100) / 100;
    if (lines.length > 0 && Math.abs(diff) >= 0.005) {
      lines[0] = {
        ...lines[0],
        amount: Math.round((lines[0].amount + diff) * 100) / 100,
      };
    }
    return lines;
  }, [taxRates, gstBase, taxAmount, additionalTaxLines]);

  // ── New invoice system (live) ──────────────────────────────
  // When a booking has an invoice row, it is the source of truth for
  // subtotal / tax / total. Legacy bookings (no invoice) fall back to the
  // booking-derived values computed above.
  const { invoice, refetch: refetchInvoice } = useBookingInvoice(bookingId);
  const useInvoice = !!invoice;

  // Issued (locked) invoice: discounts no longer flow into it, so applying or
  // deleting one from here would silently change nothing. Freeze those actions.
  const invoiceLocked = isInvoiceLocked(invoice);

  // The invoice header (total / tax / paid / balance) is recomputed by DB
  // triggers whenever line items, discounts, or PAYMENTS change. Realtime
  // usually delivers that, but it can lag or be missed on the booking modal —
  // leaving the displayed Total/Balance stale until a manual reload. So also
  // refetch the invoice whenever the line-item, booking-discount, OR payments
  // signature changes (e.g. after a discount is applied/removed or a payment
  // is applied/voided), guaranteeing the totals update in-session.
  const lineSignature = useMemo(
    () =>
      JSON.stringify([
        (lineItems || []).map((s: any) => [
          s.id,
          s.price,
          s.quantity,
          s.discount_amount,
          s.discount_value,
          s.discount_type,
          (s.addons || s.service_addons || []).map((a: any) => [
            a.id,
            a.price,
            a.discount_amount,
            a.discount_value,
            a.discount_type,
          ]),
        ]),
        // Booking-wide discount signature: applying/removing a discount
        // recomputes the invoice server-side — refetch so tax/total/balance
        // reflect it immediately (this was the stale-summary bug on 8198).
        (discounts || []).map((d) => [d.id, d.type, d.amount]),
        discountAmountFromBooking ?? null,
        // Payments signature: applying/voiding a payment changes the invoice
        // rollup server-side — refetch so paid/balance reflect it immediately.
        (payments || []).map((p: any) => [p.id, p.status, p.amount, p.tip_amount, p.surcharge_amount]),
      ]),
    [lineItems, discounts, discountAmountFromBooking, payments],
  );

  const signatureInitializedRef = useRef(false);
  useEffect(() => {
    if (!bookingId) return;
    // Skip the first run — the hook already fetches on mount.
    if (!signatureInitializedRef.current) {
      signatureInitializedRef.current = true;
      return;
    }
    refetchInvoice();
  }, [lineSignature, bookingId, refetchInvoice]);

  // Per-line (service + add-on) discounts fold into the displayed subtotal so it
  // reflects discounted pricing; only the booking-wide discount shows as its own
  // line. Matches the SQL triggers, so subtotal − booking discount + tax = total.
  const lineDiscountTotalCents = useMemo(
    () => (useInvoice ? totalLineDiscountCents(lineItems) : 0),
    [useInvoice, lineItems],
  );

  const viewSubtotal = useInvoice
    ? (invoice!.subtotal_cents - lineDiscountTotalCents) / 100
    : (subtotal || 0);
  const viewTotal = useInvoice ? invoice!.total_cents / 100 : totalAmount;

  const viewTaxLines = useMemo<{ label: string; amount: number }[]>(() => {
    if (useInvoice) {
      return (invoice!.tax_breakdown || [])
        .filter((c) => c.amount_cents !== 0)
        .map((c) => ({
          label:
            c.base_cents != null
              ? `${c.name} (${formatRateBps(c.rate_bps)}% on ${fmt(c.base_cents / 100)})`
              : `${c.name} (${formatRateBps(c.rate_bps)}%)`,
          amount: c.amount_cents / 100,
        }));
    }
    return [
      ...businessRateLines,
      ...additionalTaxLines.map((l) => ({
        label: `${l.name} (${l.rate}% on ${fmt(l.taxableBase ?? 0)})`,
        amount: l.amount,
      })),
    ];
  }, [useInvoice, invoice, businessRateLines, additionalTaxLines]);

  const viewTaxTotal = useInvoice ? invoice!.tax_cents / 100 : taxAmount;

  const completedPayments = payments.filter((p) => isPaymentCountedForBalance(p.status));

  /** Amount applied to booking balance (excludes card processing surcharge). */
  const totalPaidLegacy = completedPayments.reduce((sum, p) => sum + paymentPrincipal(p), 0);
  const totalSurchargesLegacy = completedPayments.reduce((sum, p) => sum + (Number(p.surcharge_amount) || 0), 0);
  const totalTipsLegacy = completedPayments.reduce((sum, p) => sum + (Number(p.tip_amount) || 0), 0);

  // Paid / balance / tips / surcharge come from the invoice's server-side rollup
  // when present, falling back to the JS heuristic for invoice-less bookings.
  const totalPaid = useInvoice ? invoice!.amount_paid_cents / 100 : totalPaidLegacy;
  const totalTips = useInvoice ? invoice!.tips_cents / 100 : totalTipsLegacy;
  const totalSurcharges = useInvoice ? invoice!.surcharge_cents / 100 : totalSurchargesLegacy;
  const balanceDue = useInvoice
    ? Math.max(0, invoice!.amount_due_cents / 100)
    : Math.max(0, Math.round((viewTotal - totalPaidLegacy) * 100) / 100);
  const hasBalance = balanceDue > 0;

  /**
   * Gross collected from customers (principal + surcharge + tips). With an
   * invoice we sum the rollup pieces; legacy uses sum(amount) + tips.
   */
  const totalReceived = useInvoice
    ? totalPaid + totalSurcharges + totalTips
    : completedPayments.reduce((sum, p) => sum + paymentGross(p), 0) + totalTipsLegacy;

  if (compact) {
    return (
      <div
        className="backdrop-blur-2xl bg-white/95 border-t border-gray-300/40"
        style={{ boxShadow: '0 -8px 30px rgba(0,0,0,0.08)', WebkitBackdropFilter: 'blur(40px)', backdropFilter: 'blur(40px)' }}
      >
        {/* Expandable breakdown */}
        {expanded && (
          <div className="px-5 pt-3 pb-2 space-y-2 animate-[fadeIn_0.15s_ease-out]">
            <div className="flex justify-between text-[14px]">
              <span className="text-gray-500">Subtotal</span>
              <span className="text-gray-900 font-medium">{fmt(viewSubtotal)}</span>
            </div>
            {discounts?.map((discount) => (
              <div key={discount.id} className="flex justify-between text-[14px]">
                <span className="text-red-500">{discount.title} ({discount.type === 'percentage' ? `${discount.amount}%` : '$'})</span>
                <div className="flex items-center gap-2.5">
                  <span className="text-red-500 font-medium">-{fmt(discount.type === 'percentage' ? (viewSubtotal * discount.amount / 100) : discount.amount)}</span>
                  {!invoiceLocked && (
                    <button onClick={() => onDeleteDiscount(discount.id)} className="text-red-400 active:text-red-600 p-1 -mr-1">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {viewTaxLines.map((line, i) => (
              <div key={`tax-${i}`} className="flex justify-between text-[14px]">
                <span className="text-gray-500">{line.label}</span>
                <span className="text-gray-900 font-medium">{fmt(line.amount)}</span>
              </div>
            ))}
            {viewTaxLines.length === 0 && viewTaxTotal > 0 && (
              <div className="flex justify-between text-[14px]">
                <span className="text-gray-500">Tax</span>
                <span className="text-gray-900 font-medium">{fmt(viewTaxTotal)}</span>
              </div>
            )}
            {totalPaid > 0 && (
              <div className="flex justify-between text-[14px]">
                <span className="text-green-600">Paid</span>
                <span className="text-green-600 font-semibold">{fmt(totalPaid)}</span>
              </div>
            )}
            {totalSurcharges > 0 && (
              <div className="flex justify-between text-[14px]">
                <span className="text-amber-600">Processing Fees</span>
                <span className="text-amber-600 font-semibold">{fmt(totalSurcharges)}</span>
              </div>
            )}
            {totalTips > 0 && (
              <div className="flex justify-between text-[14px]">
                <span className="text-green-600">Tips</span>
                <span className="text-green-600 font-semibold">{fmt(totalTips)}</span>
              </div>
            )}
            <div className="pt-1.5 pb-0.5 border-t border-gray-200/50">
              {invoiceLocked ? (
                <div className="flex items-center justify-center gap-1.5 py-1.5 text-[13px] font-medium text-gray-400">
                  <Lock className="h-3.5 w-3.5" />
                  Invoice is final — billing locked
                </div>
              ) : (
                <button
                  onClick={onApplyDiscount}
                  className="w-full text-center text-[15px] font-medium text-blue-500 active:text-blue-600 py-1.5"
                >
                  Apply Discount
                </button>
              )}
            </div>
          </div>
        )}

        {/* Single-line compact bar — entire row toggles expand/collapse */}
        <div
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse payment details' : 'Expand payment details'}
          onClick={() => setExpanded(!expanded)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpanded(!expanded);
            }
          }}
          className="flex items-center gap-3 px-4 py-2.5 cursor-pointer active:bg-gray-100/50 transition-colors touch-manipulation select-none"
          style={{
            paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom, 0.625rem))',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Total</span>
              <span className="text-[16px] font-bold text-gray-900 tracking-tight">{fmt(viewTotal)}</span>
            </div>
            <div className="w-px h-8 bg-gray-200/80 flex-shrink-0" />
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Balance</span>
              <span className={'text-[16px] font-bold tracking-tight ' + (hasBalance ? 'text-red-500' : 'text-green-500')}>
                {hasBalance ? fmt(balanceDue) : 'Paid'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {onViewInvoice && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewInvoice();
                }}
                className="h-[40px] w-[40px] flex items-center justify-center rounded-xl active:scale-[0.93] transition-all touch-manipulation"
                style={{ backgroundColor: 'rgba(120,120,128,0.12)', WebkitTapHighlightColor: 'transparent' }}
                aria-label="Invoice"
              >
                <FileText className="h-[18px] w-[18px] text-gray-600" />
              </button>
            )}
            {onRecordPayment && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRecordPayment();
                }}
                className="h-[40px] px-5 flex items-center justify-center rounded-xl text-white text-[15px] font-semibold active:scale-[0.95] transition-all touch-manipulation"
                style={{ backgroundColor: '#007AFF', WebkitTapHighlightColor: 'transparent' }}
                aria-label="Take Payment"
              >
                Pay
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Original full layout (desktop)
  return (
    <div>
      <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-sm font-semibold text-gray-700">Payment Summary</h3>
        {invoiceLocked ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-gray-900 text-white" title="Invoice is final — unlock it to change billing">
            <Lock className="h-2.5 w-2.5" />
            Final
          </span>
        ) : (
          <button
            type="button"
            onClick={onApplyDiscount}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
          >
            Apply Discount
          </button>
        )}
      </div>
      <div className="p-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal</span>
            <span>{fmt(viewSubtotal)}</span>
          </div>
          {discounts?.map((discount) => (
            <div key={discount.id} className="flex justify-between text-sm text-gray-600">
              <span className="text-red-600">
                {discount.title} ({discount.type === 'percentage' ? `${discount.amount}%` : '$'})
              </span>
              <div className="flex items-center space-x-2 text-red-600">
                <span>-{fmt(discount.type === 'percentage' ? (viewSubtotal * discount.amount / 100) : discount.amount)}</span>
                {!invoiceLocked && (
                  <button 
                    onClick={() => onDeleteDiscount(discount.id)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {viewTaxLines.map((line, i) => (
            <div key={`tax-full-${i}`} className="flex justify-between text-sm text-gray-600">
              <span>{line.label}</span>
              <span>{fmt(line.amount)}</span>
            </div>
          ))}
          {viewTaxLines.length === 0 && viewTaxTotal > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>Tax</span>
              <span>{fmt(viewTaxTotal)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm text-green-600">
            <span>Amount Paid</span>
            <span>{fmt(totalPaid)}</span>
          </div>
          {totalSurcharges > 0 && (
            <div className="flex justify-between text-sm text-amber-600">
              <span>Card Processing Fees</span>
              <span>{fmt(totalSurcharges)}</span>
            </div>
          )}
          {totalTips > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Tips Received</span>
              <span>{fmt(totalTips)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-medium text-gray-900 pt-2 border-t border-gray-200">
            <span>Total Received</span>
            <span>{fmt(totalReceived)}</span>
          </div>
          <div className="flex justify-between text-sm font-medium text-gray-900">
            <span>Balance Due</span>
            <span>{fmt(balanceDue)}</span>
          </div>
          {(onRecordPayment || onViewInvoice) && (
            <div className="pt-3 flex flex-col gap-2" style={{
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              marginBottom: 'env(safe-area-inset-bottom, 0px)'
            }}>
              {onRecordPayment && (
                <button
                  type="button"
                  onClick={onRecordPayment}
                  className="w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                >
                  Take Payment
                </button>
              )}
              {onViewInvoice && (
                <button
                  type="button"
                  onClick={onViewInvoice}
                  className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-all active:scale-[0.98]"
                >
                  Invoice
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
